import SimpleModal from "app/components/Modals/SimpleModal";
import Button from "app/storybookComponents/Button";
import { ManagerCustomListItem } from "../types";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  selectAllCompanyUsersById,
  selectTeamsByTeamId,
} from "app/containers/Global/slice";
import { selectDepartments } from "app/containers/AdminConsole/slice";
import { getPeopleOrPerson, getS } from "utils/helperFunctions";
import SimpleModalFooter from "app/components/Modals/SimpleModalFooter";
import {
  updateCompanyWeeklyCheckInCustomList,
  updateWeeklyCheckInCompanySettings,
} from "../slice";
import { weeklyCheckInReportSettingsApi } from "app/containers/AdminConsole/weeklyCheckInReportSettingsApi";
import { useMemo, useState } from "react";
import CircleEditButton from "app/storybookComponents/Button/CircleEditButton";
import { getTotalPeopleInSelectedEntitiesFromCustomList } from "../helpers";
import { selectAllUserSubordinates } from "app/containers/UserSettings/slice";
import { WEEKLY_CHECK_IN_FEATURE_NAME } from "../constants";

interface Props {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  onEditButtonClick?: () => void;
  teamIds?: number[];
  departmentIds?: number[];
  managers?: ManagerCustomListItem[];
  isCompanyWide?: boolean;
}

const CheckinCustomListConfirmationModal = ({
  show,
  onHide,
  onEditButtonClick,
  teamIds = [],
  departmentIds = [],
  managers = [],
  isCompanyWide,
  onSuccess,
}: Readonly<Props>) => {
  const dispatch = useAppDispatch();
  const teams = useAppSelector(selectTeamsByTeamId);
  const departments = useAppSelector(selectDepartments);
  const users = useAppSelector(selectAllCompanyUsersById);
  const allUsersSubordinates = useAppSelector(selectAllUserSubordinates);
  const [loading, setLoading] = useState(false);

  const totalPeopleInSelectedEntitiesCount = useMemo(() => {
    if (isCompanyWide) {
      return Object.keys(users).length;
    }
    return getTotalPeopleInSelectedEntitiesFromCustomList(
      {
        managers,
        teams: teamIds,
        departments: departmentIds,
      },
      allUsersSubordinates,
      teams,
      departments
    );
  }, [
    allUsersSubordinates,
    managers,
    teamIds,
    departmentIds,
    teams,
    departments,
    isCompanyWide,
    users,
  ]);

  const getSendingToText = () => {
    if (isCompanyWide) {
      return "Everyone in your organization";
    }
    const managersWithStructure = managers.filter(
      (manager) => manager.entireReportingStructure
    );
    const managersWithDirectReports = managers.filter(
      (manager) => !manager.entireReportingStructure
    );

    // Build text parts
    const textParts = [];

    if (managersWithStructure.length > 0) {
      const count = managersWithStructure.length;
      textParts.push(
        `${count} manager${getS(
          count
        )} and their entire reporting structure${getS(count)}`
      );
    }

    if (managersWithDirectReports.length > 0) {
      const count = managersWithDirectReports.length;
      textParts.push(`${count} manager${getS(count)} and their direct reports`);
    }

    if (teamIds.length > 0) {
      textParts.push(`${teamIds.length} team${getS(teamIds.length)}`);
    }

    if (departmentIds.length > 0) {
      textParts.push(
        `${departmentIds.length} department${getS(departmentIds.length)}`
      );
    }

    // Format the text with appropriate conjunctions
    if (textParts.length === 0) {
      return "";
    }

    if (textParts.length === 1) {
      return textParts[0];
    }

    if (textParts.length === 2) {
      return `${textParts[0]} and ${textParts[1]}`;
    }

    const lastPart = textParts.pop();
    return `${textParts.join(", ")}, and ${lastPart}`;
  };

  const getDescription = () => {
    if (
      !isCompanyWide &&
      !managers.length &&
      !teamIds.length &&
      !departmentIds.length
    ) {
      return null;
    }
    return (
      <p className="grey-text">
        {totalPeopleInSelectedEntitiesCount}{" "}
        {getPeopleOrPerson(totalPeopleInSelectedEntitiesCount)} total
      </p>
    );
  };

  const getUpdateWeeklyCheckInPayload = () => {
    if (isCompanyWide) {
      return {
        hasWeeklyCheckInsEnabled: true,
        hasWeeklyCheckInsEnabledForEntireCompany: true,
      };
    }

    return {
      hasWeeklyCheckInsEnabled: true,
      hasWeeklyCheckInsEnabledForEntireCompany: false,
    };
  };

  const handleConfirm = async () => {
    setLoading(true);

    await dispatch(
      updateWeeklyCheckInCompanySettings(getUpdateWeeklyCheckInPayload())
    );

    if (!isCompanyWide) {
      await dispatch(
        updateCompanyWeeklyCheckInCustomList({
          teams: teamIds,
          departments: departmentIds,
          managers: managers,
        })
      );
    }

    dispatch(
      weeklyCheckInReportSettingsApi.util.invalidateTags([
        "SeniorManagerPreview",
        "TeamLeaderPreview",
      ])
    );

    setLoading(false);
    onSuccess();
  };

  const getContent = () => {
    if (
      !isCompanyWide &&
      !managers.length &&
      !teamIds.length &&
      !departmentIds.length
    ) {
      return (
        <>
          <div style={{ textAlign: "center" }}>
            <h3>Are you sure you want to save an empty list?</h3>
            <p style={{ marginTop: "8px" }}>
              Your custom list is currently empty. This list will be
              automatically updated with all managers, teams, and departments
              with {WEEKLY_CHECK_IN_FEATURE_NAME}s enabled by you or any
              managers, team leaders, or department leaders.
            </p>
          </div>
          <div className="row-gap-10px justify-content-center mb-2">
            <Button
              onClick={onHide}
              variant="secondary-blue"
              disabled={loading}
            >
              Back
            </Button>
            <Button onClick={handleConfirm} disabled={loading}>
              Confirm
            </Button>
          </div>
        </>
      );
    }

    return (
      <>
        <h4>You're enabling {WEEKLY_CHECK_IN_FEATURE_NAME}s for:</h4>
        <div
          className="row-gap-8px justify-content-between"
          style={{ marginTop: "-4px" }}
        >
          <div className="column-gap-4px">
            <p>
              <b>{getSendingToText()}</b>
            </p>
            {getDescription()}
          </div>
          {onEditButtonClick ? (
            <CircleEditButton onClick={onEditButtonClick} />
          ) : null}
        </div>
        <hr className="m-0" />
        <SimpleModalFooter
          onPrimaryButtonClick={handleConfirm}
          primaryButtonText="Enable and confirm"
          onBackButtonClick={onHide}
          backButtonText="Back"
          isPrimaryButtonDisabled={loading}
          isBackButtonDisabled={loading}
          backButtonClassName=""
        />
      </>
    );
  };

  return (
    <SimpleModal show={show} onHide={onHide} title="Confirmation">
      <hr className="m-0" />
      {getContent()}
    </SimpleModal>
  );
};

export default CheckinCustomListConfirmationModal;
